import {Dispatch, MutableRefObject, useEffect} from 'react';

import {Action, DrawResult, DrawingActionKind} from '../types';

/**
 * Custom hook for handling events related to the DrawingManager
 * @param drawingManager - The DrawingManager instance
 * @param overlaysShouldUpdateRef - A ref to track if the overlays should update
 * @param dispatch - The dispatch function to update the state
 */
export function useDrawingManagerEvents(
  drawingManager: google.maps.drawing.DrawingManager | null | undefined,
  overlaysShouldUpdateRef: MutableRefObject<boolean>,
  dispatch: Dispatch<Action>,
  initialOverlays: google.maps.MVCObject[]
) {
  // Hook to handle events related to the DrawingManager
  useEffect(() => {
    if (!drawingManager) return;

    const eventListeners: Array<google.maps.MapsEventListener> = [];

    const addUpdateListener = (eventName: string, overlay: google.maps.MVCObject) => {
      const updateListener = google.maps.event.addListener(overlay, eventName, () => {
        if (eventName === 'dragstart') {
          overlaysShouldUpdateRef.current = false;
        }

        if (eventName === 'dragend') {
          overlaysShouldUpdateRef.current = true;
        }

        if (overlaysShouldUpdateRef.current) {
          dispatch({type: DrawingActionKind.UPDATE_OVERLAYS});
        }
      });

      eventListeners.push(updateListener);
    };

    // Add event listeners for initial overlays
    initialOverlays.forEach(overlay => {
      const overlayType = getOverlayType(overlay);
      if (overlayType) {
        switch (overlayType) {
          case google.maps.drawing.OverlayType.CIRCLE:
            ['center_changed', 'radius_changed'].forEach(eventName => addUpdateListener(eventName, overlay));
            break;
          case google.maps.drawing.OverlayType.MARKER:
            ['dragend'].forEach(eventName => addUpdateListener(eventName, overlay));
            break;
          case google.maps.drawing.OverlayType.POLYGON:
          case google.maps.drawing.OverlayType.POLYLINE:
            ['mouseup'].forEach(eventName => addUpdateListener(eventName, overlay));
            break;
          case google.maps.drawing.OverlayType.RECTANGLE:
            ['bounds_changed', 'dragstart', 'dragend'].forEach(eventName => addUpdateListener(eventName, overlay));
            break;
        }
      }
    });

    const overlayCompleteListener = google.maps.event.addListener(
      drawingManager,
      'overlaycomplete',
      (drawResult: DrawResult) => {
        switch (drawResult.type) {
          case google.maps.drawing.OverlayType.CIRCLE:
            ['center_changed', 'radius_changed'].forEach(eventName =>
              addUpdateListener(eventName, drawResult.overlay as google.maps.Circle)
            );
            break;
          case google.maps.drawing.OverlayType.MARKER:
            ['dragend'].forEach(eventName => addUpdateListener(eventName, drawResult.overlay));
            break;

          case google.maps.drawing.OverlayType.POLYGON:
          case google.maps.drawing.OverlayType.POLYLINE:
            ['mouseup'].forEach(eventName =>
              addUpdateListener(eventName, drawResult.overlay as google.maps.Polygon | google.maps.Polyline)
            );
            break;

          case google.maps.drawing.OverlayType.RECTANGLE:
            ['bounds_changed', 'dragstart', 'dragend'].forEach(eventName =>
              addUpdateListener(eventName, drawResult.overlay as google.maps.Rectangle)
            );
            break;
        }

        dispatch({type: DrawingActionKind.SET_OVERLAY, payload: drawResult});
      }
    );

    eventListeners.push(overlayCompleteListener);

    return () => {
      eventListeners.forEach(listener => google.maps.event.removeListener(listener));
    };
  }, [dispatch, drawingManager, overlaysShouldUpdateRef, initialOverlays]);
}

/**
 * Get the type of overlay
 * @param overlay - The overlay to get the type of
 * @returns The type of overlay
 */
function getOverlayType(overlay: google.maps.MVCObject): google.maps.drawing.OverlayType | null {
  if (overlay instanceof google.maps.Marker) return google.maps.drawing.OverlayType.MARKER;
  if (overlay instanceof google.maps.Circle) return google.maps.drawing.OverlayType.CIRCLE;
  if (overlay instanceof google.maps.Polygon) return google.maps.drawing.OverlayType.POLYGON;
  if (overlay instanceof google.maps.Polyline) return google.maps.drawing.OverlayType.POLYLINE;
  if (overlay instanceof google.maps.Rectangle) return google.maps.drawing.OverlayType.RECTANGLE;
  return null;
}
